import { DateRangePicker } from 'react-date-range';
import { enUS, fr } from 'date-fns/locale';
import {
  format, getYear, addMonths, startOfWeek, endOfWeek, startOfYear, endOfYear,
  startOfMonth, endOfMonth, isSameDay,
} from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

// Utils & misc
import cn from '../../../utils/cn';

// Styles
import styles from './DateRangePickerDefault.module.css';

// Components
import DateRangePickerInput from '../../molecules/DateRangePickerInput/DateRangePickerInput';
import Picto from '../../atoms/Picto/Picto';

// Use Cases
import {
  getTrimesterRange,
  INITIAL_RANGE,
  NUMBER_MONTH_DISPLAY_BY_TYPE,
} from '../../../domain/dateRange/useDateRangeUseCase';

/**
 * @param {object} props
 * @param {import('react-date-range').Range[]} props.ranges
 * @param {(ranges: import('react-date-range').Range[]) => void} props.handleRangesChange
 * @param {React.ReactNode} [props.children=null]
 * @param {string} [props.locale]
 * @param {React.ReactNode} props.sidebar
 * @param {import('react-date-range').RangeFocus} props.focus
 * @param {(focus: import('react-date-range').RangeFocus) => void} props.handleFocusChange
 * @param {ApiManagerDateRangeType} props.type
 * @param {Partial<Record<ApiManagerDateRangeType, number>>} [props.numberMonthDisplayByType=NUMBER_MONTH_DISPLAY_BY_TYPE]
 * @param {Date} [props.selectedDate] - Anchored date to highlight within the selected range
 * @returns {React.ReactElement}
 */
function DateRangePickerDefault({
  locale = 'fr',
  sidebar,
  children = null,
  type,
  ranges,
  handleRangesChange,
  focus,
  handleFocusChange,
  numberMonthDisplayByType = NUMBER_MONTH_DISPLAY_BY_TYPE,
  selectedDate,
}) {
  /**
   * Initial preview state for date range selection
   * @type {import('react-date-range').Preview}
   */
  const initPreview = {
    startDate: null,
    endDate: null,
    color: 'default',
  };
  /**
   * Preview state for date range selection
   * @type {[import('react-date-range').Preview, React.Dispatch<React.SetStateAction<import('react-date-range').Preview>>]}
   */
  const [preview, setPreview] = useState(null);

  const mergedNumberMonthDisplayByType = useMemo(() => ({
    ...NUMBER_MONTH_DISPLAY_BY_TYPE,
    ...numberMonthDisplayByType,
  }), [numberMonthDisplayByType]);

  const localeDate = useMemo(() => {
    switch (locale) {
      case 'fr':
        return fr;
      default:
        return enUS;
    }
  }, [locale]);

  const monthByType = useMemo(() => mergedNumberMonthDisplayByType[type], [type, mergedNumberMonthDisplayByType]);

  const renderDayContent = useCallback((/** @type {Date} */ day) => {
    if (!selectedDate || !isSameDay(day, selectedDate)) {
      return null;
    }

    return (
      <span className="heeds-selected-date">
        {format(day, 'd', { locale: localeDate })}
      </span>
    );
  }, [selectedDate, localeDate]);

  /**
   * Handle the navigator renderer
   * @param {Date} currentFocusedDate
   * @param {(value: Date | number | string, mode?: "set" | "setYear" | "setMonth" | "monthOffset") => void} changeShownDate
   * @returns {React.ReactElement}
   */
  const navigatorRenderer = (currentFocusedDate, changeShownDate) => {
    /**
     * Handle navigation
     * @param {'minus' | 'plus'} direction
     */
    const handleNavigation = (direction) => {
      const value = mergedNumberMonthDisplayByType[type];
      if (type === 'day') {
        const addOneDay = addMonths(currentFocusedDate, direction === 'minus' ? -value : value);
        changeShownDate(addOneDay);
      }
      if (type === 'week') {
        const addOneWeek = addMonths(currentFocusedDate, direction === 'minus' ? -value : value);
        changeShownDate(addOneWeek);
      }
      if (type === 'month') {
        const addOneMonth = addMonths(currentFocusedDate, direction === 'minus' ? -value : value);
        changeShownDate(addOneMonth);
      }
      if (type === 'year') {
        const subOneYear = getYear(currentFocusedDate) + (direction === 'minus' ? -1 : 1); // 1 because we want to go to the previous year
        changeShownDate(new Date(`01/01/${subOneYear}`));
      }
      if (type === 'quarter') {
        const addSixMonths = addMonths(currentFocusedDate, direction === 'minus' ? -value : value);
        changeShownDate(addSixMonths);
      }
      if (type === 'custom') {
        const addOneMonth = addMonths(currentFocusedDate, direction === 'minus' ? -value : value);
        changeShownDate(addOneMonth);
      }
    };

    return (
      <div className={styles.navigator}>
        <button type="button" onClick={() => handleNavigation('minus')} className={styles.navigatorButton}>
          <Picto icon="chevron-left-range" className={styles.navigatorIcon} />
        </button>
        <button type="button" onClick={() => handleNavigation('plus')} className={styles.navigatorButton}>
          <Picto icon="chevron-right-range" className={styles.navigatorIcon} />
        </button>
      </div>
    );
  };

  /**
   * Handle the preview change
   * @param {Date} hoveredDate
   */
  const handlePreviewChange = (hoveredDate /** @type {Date} */) => {
    if (!hoveredDate || hoveredDate === undefined) setPreview(null);

    if (type === 'week') {
      setPreview({
        ...initPreview,
        startDate: startOfWeek(hoveredDate),
        endDate: endOfWeek(hoveredDate),
      });
      return;
    }
    if (type === 'month') {
      setPreview({
        ...initPreview,
        startDate: startOfMonth(hoveredDate),
        endDate: endOfMonth(hoveredDate),
      });
      return;
    }
    if (type === 'quarter') {
      const quarterRange = getTrimesterRange(hoveredDate);
      setPreview({
        ...initPreview,
        startDate: quarterRange.startDate,
        endDate: quarterRange.endDate,
      });
      return;
    }
    if (type === 'year') {
      setPreview({
        ...initPreview,
        startDate: startOfYear(hoveredDate),
        endDate: endOfYear(hoveredDate),
      });
      return;
    }
    // Custom range
    if (focus[1] === 0) {
      setPreview({
        ...initPreview,
        startDate: hoveredDate,
        endDate: hoveredDate,
      });
    } else {
      setPreview({
        ...initPreview,
        startDate: preview.startDate,
        endDate: hoveredDate,
      });
    }
  };

  return (
    <div className={cn([
      styles.wrapper,
      styles[type],
      styles[`view-months-${mergedNumberMonthDisplayByType[type]}`],
    ])}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          {sidebar}
        </div>

        <div className={cn([styles.picker])}>
          <div className={styles['picker-inputs']}>
            <DateRangePickerInput
              isFocused={focus[1] === 0}
              onClick={() => handleFocusChange([0, 0])}
            >
              {format(ranges[0].startDate, 'dd/MM/yyyy', { locale: localeDate })}
            </DateRangePickerInput>
            {type === 'day' ? null : (
              <DateRangePickerInput
                isFocused={focus[1] === 1}
                onClick={() => handleFocusChange([0, 1])}
              >
                {format(ranges[0].endDate, 'dd/MM/yyyy', { locale: localeDate })}
              </DateRangePickerInput>
            )}
          </div>

          {/* @ts-ignore */}
          <DateRangePicker
            locale={localeDate}
            onChange={(rangesByKey) => handleRangesChange([rangesByKey[INITIAL_RANGE.key]])}
            // @ts-ignore
            showSelectionPreview
            moveRangeOnFirstSelection={false}
            months={monthByType}
            ranges={ranges}
            direction="horizontal"
            showMonthAndYearPickers={false}
            staticRanges={[]}
            inputRanges={[]}
            showDateDisplay={false}
            focusedRange={focus}
            onRangeFocusChange={handleFocusChange}
            showMonthArrow
            navigatorRenderer={navigatorRenderer}
            onPreviewChange={(
              hoveredDate, /** @type {Date} */
            // @ts-ignore
            ) => handlePreviewChange(hoveredDate)}
            preview={preview?.startDate ? preview : undefined}
            dayContentRenderer={selectedDate ? renderDayContent : undefined}
          />
        </div>
      </div>

      <div className={styles.footer}>
        {children}
      </div>
    </div>
  );
}

export default DateRangePickerDefault;
